import React, { CSSProperties, FC, MutableRefObject, ReactElement, RefObject, useEffect, useRef } from 'react'
import { Collapse, Fade, Grow, Popper, Zoom } from '@material-ui/core'
import { PopperPlacementType } from '@material-ui/core/Popper/Popper'
import Slide from '@material-ui/core/Slide'
import { fromEvent, Subscription } from 'rxjs'
import { StateSetter } from '../../../../React/Common/Types/Types'

type Props = {
    isOpen: boolean
    setIsOpen?: StateSetter<boolean>
    anchorEl: RefObject<HTMLElement>
    children: ReactElement | string
    Transition?: typeof Collapse | typeof Fade | typeof Grow | typeof Slide | typeof Zoom
    className?: string
    style?: CSSProperties
    placement?: PopperPlacementType
    timeout?: number
    clickAwayCallBack?: (event: MouseEvent) => void
    clickAwayGuard?: (event: MouseEvent) => boolean
    keepMounted?: boolean
}

export const TransitionPopper: FC<Props> = ({
    isOpen,
    setIsOpen,
    anchorEl,
    children,
    Transition = Fade,
    className,
    style,
    placement = 'bottom-start',
    timeout = 200,
    clickAwayCallBack,
    clickAwayGuard,
    keepMounted = false
}: Props): ReactElement => {
    const clickAwaySubscription: MutableRefObject<Nullable<Subscription>> = useRef()

    const handleOnClickAway = (event: MouseEvent): void => {
        if (
            (clickAwayGuard && !clickAwayGuard(event)) ||
            anchorEl.current?.contains(event.target as Node) ||
            anchorEl.current?.isSameNode(event.target as Node)
        ) {
            return
        }

        // Close Popper
        setIsOpen?.(false)

        if (clickAwayCallBack) {
            clickAwayCallBack(event)
        }
    }

    useEffect((): void => {
        clickAwaySubscription?.current?.unsubscribe()

        if (isOpen) {
            clickAwaySubscription.current = fromEvent(document, 'mousedown').subscribe((event: Event): void => {
                if ((event.target as HTMLElement).closest('.transition-popper')) {
                    return
                }

                handleOnClickAway(event as MouseEvent)
            })
        }
    }, [isOpen])

    return (
        <Popper
            className={`transition-popper ${className}`}
            open={isOpen}
            anchorEl={anchorEl.current}
            style={style}
            placement={placement}
            keepMounted={keepMounted}
            transition
        >
            {({
                TransitionProps
            }: {
                TransitionProps?: {
                    in: boolean
                    onEnter: () => unknown
                    onExited: () => unknown
                }
            }): ReactElement => (
                <Transition {...TransitionProps} timeout={timeout}>
                    <>{children}</>
                </Transition>
            )}
        </Popper>
    )
}
